/**
 * Toolbar chip that multi-selects site groups to scope the operations
 * dashboard. Opens a search-filterable checklist popper with a draft/apply
 * flow so unrelated re-renders don't fire a fetch per keystroke — closing
 * without an explicit Apply still commits the draft (click-away-to-apply).
 *
 * Contains:
 * - SiteGroupFilterChip — default export
 */

import HubIcon from '@mui/icons-material/Hub';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { toolbarChipSx } from '@sophocles/data-grids/components/toolbar/toolbarStyles';

export default function SiteGroupFilterChip({ siteGroups, selectedIds, onChange, label }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [draft, setDraft] = React.useState(selectedIds);
  const open = Boolean(anchorEl);
  const selected = siteGroups.filter(sg => selectedIds.includes(sg.id));
  const draftSelected = siteGroups.filter(sg => draft.includes(sg.id));
  const chipLabel = selected.length > 0 ? `${label}: ${selected.map(sg => sg.name).join(', ')}` : label;
  const dirty = JSON.stringify([...draft].sort()) !== JSON.stringify([...selectedIds].sort());

  const handleOpen = e => {
    setDraft(selectedIds);
    setAnchorEl(e.currentTarget);
  };

  const handleApply = () => {
    onChange(draft);
    setAnchorEl(null);
  };

  const handleClose = () => {
    if (dirty) onChange(draft);
    setAnchorEl(null);
  };

  return (
    <>
      <Chip
        icon={<HubIcon sx={{ fontSize: 16 }} />}
        label={chipLabel}
        size="small"
        color={selectedIds.length > 0 ? 'primary' : 'default'}
        variant={selectedIds.length > 0 ? 'filled' : 'outlined'}
        onClick={handleOpen}
        onDelete={selectedIds.length > 0 ? () => onChange([]) : undefined}
        sx={{ ...toolbarChipSx, maxWidth: 280 }}
      />
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
        modifiers={[{ name: 'preventOverflow', options: { padding: 8 } }]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={8}
            sx={{
              width: 'min(320px, calc(100vw - 16px))',
              mt: 0.5,
              borderRadius: 2,
              overflow: 'hidden',
              '& .MuiAutocomplete-listbox': { maxHeight: 280, py: 0.5 },
              '& .MuiAutocomplete-popper': {
                position: 'relative !important',
                transform: 'none !important',
                width: '100% !important',
              },
              '& .MuiAutocomplete-popper .MuiPaper-root': {
                boxShadow: 'none',
                borderRadius: 0,
              },
            }}
          >
            <Autocomplete
              multiple
              open
              size="small"
              options={siteGroups}
              value={draftSelected}
              onChange={(_, newValue) => setDraft(newValue.map(sg => sg.id))}
              getOptionLabel={sg => (sg.description ? `${sg.name} — ${sg.description}` : sg.name)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disableCloseOnSelect
              disablePortal
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Search..."
                  variant="outlined"
                  autoFocus
                  sx={{
                    px: 1,
                    pt: 1,
                    pb: 0.5,
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.8rem',
                      bgcolor: 'var(--color-gray-100)',
                      borderRadius: 'var(--radius-lg)',
                    },
                  }}
                />
              )}
              renderOption={(props, option, { selected: checked }) => (
                <li {...props} key={option.id}>
                  <Checkbox size="small" checked={checked} sx={{ p: 0, mr: 1 }} />
                  <ListItemText
                    primary={option.name}
                    secondary={option.description || null}
                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </li>
              )}
              renderTags={() => null}
              sx={{ '& .MuiAutocomplete-popupIndicator': { display: 'none' } }}
              slotProps={{ paper: { elevation: 0, sx: { boxShadow: 'none', borderRadius: 0 } } }}
            />
            {dirty && (
              <Box
                sx={{
                  px: 1,
                  py: 0.75,
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  onClick={handleApply}
                  sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                >
                  {t('operationsDashboard.apply', 'Apply')}
                </Button>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
